import { useState, useEffect } from 'react';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  MessageSquare,
  Search,
  Filter,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Copy,
  Eye,
  Smartphone,
  Database
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SMSInbox = () => {
  const { isDarkMode } = useAdminTheme();
  const { isSuperAdmin } = useAdminAuth();
  const [smsRecords, setSmsRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [networkFilter, setNetworkFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSMS, setSelectedSMS] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchSMSRecords();
    fetchStats();
  }, [statusFilter, networkFilter]);

  const fetchSMSRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (networkFilter !== 'all') params.append('network', networkFilter);
      params.append('limit', '200');
      
      const response = await axios.get(`${API}/admin/sms/inbox?${params}`, { withCredentials: true });
      setSmsRecords(response.data);
    } catch (error) {
      console.error('Error fetching SMS records:', error);
      toast.error('Failed to load SMS inbox');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/sms/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching SMS stats:', error);
    }
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/sms/seed-demo`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchSMSRecords();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const openDetails = (sms) => {
    setSelectedSMS(sms);
    setDetailsOpen(true);
  };

  const filteredRecords = smsRecords.filter(sms => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sms.sms_id?.toLowerCase().includes(query) ||
      sms.raw_message?.toLowerCase().includes(query) ||
      sms.parsed_reference?.toLowerCase().includes(query) ||
      sms.parsed_sender?.toLowerCase().includes(query) ||
      sms.phone_number?.includes(query)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'matched': return 'text-emerald-600 bg-emerald-50';
      case 'pending': return 'text-amber-600 bg-amber-50';
      case 'manual_review': return 'text-blue-600 bg-blue-50';
      case 'rejected': return 'text-red-600 bg-red-50';
      case 'duplicate': return isDarkMode ? 'text-slate-400 bg-slate-700' : 'text-gray-600 bg-gray-100';
      default: return isDarkMode ? 'text-slate-400 bg-slate-700' : 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched': return <CheckCircle2 className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'manual_review': return <AlertTriangle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'duplicate': return <Copy className="w-4 h-4" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US').format(amount);
  };

  return (
    <div data-testid="sms-inbox-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            SMS Inbox
          </h1>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            View and manage incoming mobile money SMS messages
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin() && (
            <Button
              variant="outline"
              className={`transition-all duration-200 hover:scale-105 ${
                isDarkMode 
                  ? 'border-slate-600 text-slate-300 hover:bg-slate-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              onClick={seedDemoData}
              disabled={seeding}
              data-testid="seed-demo-btn"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              Seed Demo Data
            </Button>
          )}
          <Button
            variant="outline"
            className={`transition-all duration-200 hover:scale-105 ${
              isDarkMode 
                ? 'border-slate-600 text-slate-300 hover:bg-slate-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => { fetchSMSRecords(); fetchStats(); }}
            data-testid="refresh-sms-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, icon: MessageSquare, color: 'text-teal-600' },
            { label: 'Matched', value: stats.matched, icon: CheckCircle2, color: 'text-emerald-500' },
            { label: 'Pending Review', value: stats.pending_review, icon: AlertTriangle, color: 'text-blue-500' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-500' },
            { label: 'Match Rate', value: `${stats.match_rate}%`, icon: Smartphone, color: 'text-purple-500' }
          ].map((stat, idx) => (
            <div 
              key={stat.label}
              className={`rounded-xl p-4 border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                isDarkMode 
                  ? 'bg-slate-800 border-slate-700 hover:border-slate-600' 
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  {stat.label}
                </span>
                <stat.icon className={`w-5 h-5 ${stat.color} transition-transform duration-300 hover:scale-110`} />
              </div>
              <p className={`text-2xl font-bold ${
                stat.label === 'Match Rate' ? stat.color : (isDarkMode ? 'text-white' : 'text-gray-900')
              }`}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 group">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors duration-200 ${
            isDarkMode ? 'text-slate-400 group-focus-within:text-teal-400' : 'text-gray-400 group-focus-within:text-teal-500'
          }`} />
          <Input
            placeholder="Search SMS..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-10 transition-all duration-200 ${
              isDarkMode 
                ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            data-testid="sms-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger 
            className={`w-full sm:w-48 ${
              isDarkMode 
                ? 'bg-slate-800 border-slate-600 text-white' 
                : 'bg-white border-gray-300 text-gray-700'
            }`} 
            data-testid="sms-status-filter"
          >
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
            <SelectItem value="all" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>All Status</SelectItem>
            <SelectItem value="matched" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Matched</SelectItem>
            <SelectItem value="manual_review" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Pending Review</SelectItem>
            <SelectItem value="rejected" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Rejected</SelectItem>
            <SelectItem value="duplicate" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Duplicate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={networkFilter} onValueChange={setNetworkFilter}>
          <SelectTrigger 
            className={`w-full sm:w-36 ${
              isDarkMode 
                ? 'bg-slate-800 border-slate-600 text-white' 
                : 'bg-white border-gray-300 text-gray-700'
            }`} 
            data-testid="sms-network-filter"
          >
            <SelectValue placeholder="Network" />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
            <SelectItem value="all" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>All Networks</SelectItem>
            <SelectItem value="MTN" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>MTN</SelectItem>
            <SelectItem value="Orange" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Orange</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* SMS List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
        }`}>
          <MessageSquare className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            No SMS records found
          </h3>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>
            {smsRecords.length === 0 
              ? "Click 'Seed Demo Data' to add sample SMS records for testing"
              : "Try adjusting your search or filters"}
          </p>
        </div>
      ) : (
        <div className={`rounded-xl border overflow-hidden shadow-sm ${
          isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
        }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isDarkMode ? 'bg-slate-700/50 border-b border-slate-600' : 'bg-gray-50 border-b border-gray-200'}>
                <tr>
                  <th className={`text-left text-sm font-medium px-6 py-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>SMS</th>
                  <th className={`text-left text-sm font-medium px-6 py-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Parsed Data</th>
                  <th className={`text-left text-sm font-medium px-6 py-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Network</th>
                  <th className={`text-left text-sm font-medium px-6 py-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Status</th>
                  <th className={`text-left text-sm font-medium px-6 py-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Received</th>
                  <th className={`text-right text-sm font-medium px-6 py-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                {filteredRecords.map((sms, idx) => (
                  <tr 
                    key={sms.sms_id} 
                    className={`transition-all duration-300 cursor-pointer ${
                      isDarkMode 
                        ? 'hover:bg-gradient-to-r hover:from-transparent hover:via-slate-700/50 hover:to-transparent' 
                        : 'hover:bg-gradient-to-r hover:from-transparent hover:via-teal-50/50 hover:to-transparent'
                    }`}
                    data-testid={`sms-row-${sms.sms_id}`}
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {sms.raw_message}
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                          {sms.phone_number}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                          LRD {formatCurrency(sms.parsed_amount)}
                        </p>
                        <p className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                          {sms.parsed_reference || '-'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        sms.network === 'MTN' ? 'bg-yellow-50 text-yellow-700' : 'bg-orange-50 text-orange-700'
                      }`}>
                        {sms.network}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(sms.status)}`}>
                        {getStatusIcon(sms.status)}
                        {sms.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {formatDate(sms.received_at)}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        variant="ghost"
                        className={`transition-all duration-200 hover:scale-110 ${
                          isDarkMode 
                            ? 'text-slate-400 hover:text-teal-400 hover:bg-slate-700' 
                            : 'text-gray-500 hover:text-teal-600 hover:bg-teal-50'
                        }`}
                        onClick={() => openDetails(sms)}
                        data-testid={`view-sms-${sms.sms_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SMS Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className={`max-w-2xl ${
          isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}>
          <DialogHeader>
            <DialogTitle>SMS Details</DialogTitle>
          </DialogHeader>
          
          {selectedSMS && (
            <div className="space-y-6 animate-fadeIn">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded text-sm font-medium ${getStatusColor(selectedSMS.status)}`}>
                  {getStatusIcon(selectedSMS.status)}
                  {selectedSMS.status.replace('_', ' ')}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  selectedSMS.network === 'MTN' ? 'bg-yellow-50 text-yellow-700' : 'bg-orange-50 text-orange-700'
                }`}>
                  {selectedSMS.network}
                </span>
              </div>

              {/* Raw Message */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  Raw Message
                </h4>
                <p className={`text-sm p-3 rounded border font-mono ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-600 text-white' 
                    : 'bg-white border-gray-200 text-gray-900'
                }`}>
                  {selectedSMS.raw_message}
                </p>
              </div>

              {/* Parsed Data */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  Parsed Data
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Amount</span>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSMS.parsed_amount ? `LRD ${formatCurrency(selectedSMS.parsed_amount)}` : '-'}
                    </p>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Reference</span>
                    <p className={`font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedSMS.parsed_reference || '-'}
                    </p>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Sender</span>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {selectedSMS.parsed_sender || '-'}
                    </p>
                  </div>
                  <div>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Phone Number</span>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {selectedSMS.phone_number}
                    </p>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Received At</span>
                  <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                    {formatDate(selectedSMS.received_at)}
                  </p>
                </div>
                {selectedSMS.matched_at && (
                  <div>
                    <span className={isDarkMode ? 'text-slate-500' : 'text-gray-500'}>Processed At</span>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                      {formatDate(selectedSMS.matched_at)}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                      by {selectedSMS.matched_by_name}
                    </p>
                  </div>
                )}
              </div>

              {/* Matched Transaction */}
              {selectedSMS.matched_transaction_id && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-emerald-700 mb-2">Matched Transaction</h4>
                  <p className="text-gray-900 font-mono">{selectedSMS.matched_transaction_id}</p>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedSMS.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-700 mb-2">Rejection Reason</h4>
                  <p className="text-gray-900">{selectedSMS.rejection_reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default SMSInbox;
